import React, { useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { setPlotType } from "../logic/chartPlot"
import { setSymbolAndLegs } from "../logic/chartSymbolAndLeg"
import _ from "lodash"
import useCprovder from "../logic/useCprovider"
import useChartResize from "../logic/useChartResize"
import chartColors from "../logic/chartColor"
import { LIMIT_RENDER_COUNT_MINI, MODAL_CANVAS_ID } from "../logic/chartConfig"
import { setSubChart } from "../logic/chartSub"
import { setAddonChart, removeAddonChart } from "../logic/chartAddon"
import { setKVOnOff } from "components/atoms/chart/logic/chartTools"

interface Window {
  Logic: any
  IntelliFactory: any
  Trading: any
}

declare var window: Window

interface props {
  canvasId?: string
}

const SpChart = React.memo((props: props) => {
  const canvasId = props.canvasId || MODAL_CANVAS_ID
  // Selector
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const providersSelector = (state: any) => state.chart[canvasId].providers
  const chartSettingSelector = (state: any) => state.chart.chartSetting
  const symbolSelector = (state: any) => state.chart[canvasId].symbol
  const subChartSelector = (state: any) => state.chart[canvasId].subChart
  const termSelector = (state: any) => state.chart[canvasId].term
  const colorSettingSelector = (state: any) => state.chart.colorSetting
  const renderCountSelector = (state: any) => state.chart[canvasId].renderCount

  const plotType = useSelector(plotTypeSelector)
  const term = useSelector(termSelector)
  const providers = useSelector(providersSelector)
  const [_provider, _controller, _procs] = providers
  const subChart = useSelector(subChartSelector)
  const symbol = useSelector(symbolSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const chartSetting = useSelector(chartSettingSelector)
  const renderCount = useSelector(renderCountSelector)

  const [createCProvider] = useCprovder({
    canvasId: canvasId,
    miniChartFlag: true,
  })
  const { resize, removeResize, size } = useChartResize({
    canvasId: canvasId,
    parentElement: `chart-body-${canvasId}`,
  })
  const { width, height, scaleWidth, scaleHeight } = size
  const _setKVOnOff = useMemo(() => {
    return setKVOnOff(_controller)
  }, [_controller])

  const applyOption = () => {
    setPlotType(plotType.key, _controller)
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
    setSymbolAndLegs(
      term.key,
      symbol,
      _provider,
      _controller,
      LIMIT_RENDER_COUNT_MINI
    )
    _controller.Chart.set_BarNumMargin([renderCount, 5])
    _controller.Chart.set_Setting(
      window.IntelliFactory.Runtime.New(
        window.Trading.Chart.BaseTypes.ChartSetting,
        {
          EnableScrollBar: false,
          EnableSplitterMH: false,
          EnableMovePortMaxMH: false,
          EnableMoveYMinMaxPMH: false,
        }
      )
    )
    _setAddonChart()
  }

  const _setAddonChart = () => {
    setAddonChart(chartSetting, "AverageN", _controller, term.key, [
      "AverageN_1",
      "AverageN_2",
      "AverageN_3",
    ])
    _setKVOnOff("useParamList", false)
  }

  useEffect(() => {
    if (window.Logic === undefined) return
    createCProvider()
  }, [])

  useEffect(() => {
    if (!_controller) return
    applyOption()
    resize()
    return () => removeResize()
  }, [_controller])

  useEffect(() => {
    if (!_controller) return
    setSymbolAndLegs(
      term.key,
      symbol,
      _provider,
      _controller,
      LIMIT_RENDER_COUNT_MINI
    )
  }, [term, symbol])

  useEffect(() => {
    if (!_controller) return
    _setAddonChart()
  }, [symbol, term])

  useEffect(() => {
    if (!_controller) return
    // Viewを切り替える場合、reDrawする必要があるため以下3つの関数が必要
    setPlotType(plotType.key, _controller)
    setSymbolAndLegs(
      term.key,
      symbol,
      _provider,
      _controller,
      LIMIT_RENDER_COUNT_MINI
    )
    _controller.Chart.Draw()
  }, [plotType])

  useEffect(() => {
    if (!_controller) return
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
  }, [colorSetting])

  return (
    <div
      id={`chart-body-${canvasId}`}
      style={{ width: "100%", height: "100%" }}
    >
      <canvas
        id={canvasId}
        width={scaleWidth}
        height={scaleHeight}
        style={{
          cursor: "default",
          width: `${width}px`,
          height: `${height}px`,
        }}
      />
    </div>
  )
})

export default SpChart
